import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { ref, onValue, off } from 'firebase/database';
import { getDatabase } from 'firebase/database';
import { getApps, initializeApp } from 'firebase/app';
import FirebaseRealtime from '../utils/firebaseRealtime';
import BookingAPI from '../common/bookingApi';
import { FirebaseConfig } from '../config/FirebaseConfig';

/**
 * Hook sederhana untuk driver location tracking menggunakan selector
 * Fokus pada real-time tanpa throttling - SOLUSI SEDERHANA
 */
export const useDriverLocationSimple = (bookingId) => {
  const [driverLocation, setDriverLocation] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [error, setError] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [settings, setSettings] = useState(null);
  const realtimeUnsubscribe = useRef(null);
  const bookingStatusUnsubscribe = useRef(null);

  // ✅ SOLUSI: Selector untuk mendapatkan data booking dari Redux
  const selectorBookingData = useSelector((state) => {
    const activeBookings = state.bookinglistdata?.active || [];
    return activeBookings.find(booking => booking.id === bookingId);
  });

  // ✅ SOLUSI: Selector untuk settings dari Redux
  const selectorSettings = useSelector((state) => state.settingsdata?.settings);

  // ✅ SOLUSI: Fetch booking data jika tidak ada di Redux (untuk incognito mode)
  useEffect(() => {
    if (selectorBookingData) {
      console.log('📍 Using booking data from Redux selector');
      setBookingData(selectorBookingData);
    } else if (bookingId) {
      console.log('📍 Redux store empty, fetching booking data from API for incognito mode');
      const fetchBookingData = async () => {
        try {
          const result = await BookingAPI.getPublicSingleBooking(bookingId);
          if (result.success) {
            console.log('📍 API booking data for tracking:', result.data);
            
            // Format booking data seperti di bookinglistactions.js
            const formattedBookingData = {
              ...result.data,
              id: bookingId,
              pickupAddress: result.data.pickup?.add ?? '',
              dropAddress: result.data.drop?.add ?? '',
              discount: result.data.discount || 0,
              cashPaymentAmount: result.data.cashPaymentAmount || 0,
              cardPaymentAmount: result.data.cardPaymentAmount || 0
            };
            
            console.log('📍 Formatted API booking data:', formattedBookingData);
            setBookingData(formattedBookingData);
          }
        } catch (err) {
          console.error('Error fetching booking data for tracking:', err);
        }
      };
      fetchBookingData();
    }
  }, [bookingId, selectorBookingData]);

  // ✅ SOLUSI: Fetch settings jika tidak ada di Redux (untuk incognito mode)
  useEffect(() => {
    if (selectorSettings) {
      console.log('📍 Using settings from Redux selector');
      setSettings(selectorSettings);
    } else {
      console.log('📍 Redux store empty, using default settings for incognito mode');
      // Default settings untuk incognito mode - share_trip = true
      setSettings({ share_trip: true });
    }
  }, [selectorSettings]);

  // ✅ SOLUSI: Real-time listener untuk booking status (untuk update status real-time)
  useEffect(() => {
    if (!bookingId) {
      console.log('🔥 No bookingId provided, skipping booking status listener');
      return;
    }

    console.log('🔥 Setting up real-time listener for booking status:', bookingId);
    
    // Initialize Firebase if not already initialized
    let app;
    if (!getApps().length) {
      app = initializeApp(FirebaseConfig);
      console.log('🔥 Firebase app initialized for booking status listener');
    } else {
      app = getApps()[0];
      console.log('🔥 Using existing Firebase app for booking status listener');
    }
    const database = getDatabase(app);
    console.log('🔥 Database instance created:', !!database);

    // Listen to booking status changes
    const bookingRef = ref(database, `bookings/${bookingId}`);
    console.log('🔥 Booking reference created:', bookingRef.toString());
    
    bookingStatusUnsubscribe.current = onValue(bookingRef, (snapshot) => {
      console.log('🔥 Firebase snapshot received for booking status');
      console.log('🔥 Snapshot exists:', snapshot.exists());
      console.log('🔥 Snapshot key:', snapshot.key);
      console.log('🔥 Snapshot value:', snapshot.val());
      
      if (snapshot.exists()) {
        const rawBookingData = snapshot.val();
        console.log('🔥 Real-time booking status update received:', rawBookingData);
        
        // Format booking data seperti di bookinglistactions.js
        const formattedBookingData = {
          ...rawBookingData,
          id: bookingId,
          pickupAddress: rawBookingData.pickup?.add ?? '',
          dropAddress: rawBookingData.drop?.add ?? '',
          discount: rawBookingData.discount || 0,
          cashPaymentAmount: rawBookingData.cashPaymentAmount || 0,
          cardPaymentAmount: rawBookingData.cardPaymentAmount || 0
        };
        
        console.log('🔥 Formatted real-time booking data:', formattedBookingData);
        console.log('🔥 Booking status:', formattedBookingData?.status);
        console.log('🔥 Pickup address:', formattedBookingData?.pickupAddress);
        console.log('🔥 Drop address:', formattedBookingData?.dropAddress);
        setBookingData(formattedBookingData);
      } else {
        console.log('🔥 No booking data found for ID:', bookingId);
        console.log('🔥 This might be a permission issue or booking does not exist');
      }
    }, (error) => {
      console.error('🔥 Error listening to booking status:', error);
      console.error('🔥 Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      console.error('🔥 This might be a Firebase rules or connection issue');
    });

    console.log('🔥 Booking status listener setup completed');

    // Cleanup
    return () => {
      if (bookingStatusUnsubscribe.current) {
        console.log('🔥 Cleaning up booking status listener');
        bookingStatusUnsubscribe.current();
        bookingStatusUnsubscribe.current = null;
      }
    };
  }, [bookingId]);

  // ✅ SOLUSI: Check if tracking should be active
  const shouldTrack = bookingData && 
    ['ACCEPTED', 'STARTED'].includes(bookingData.status) &&
    settings?.share_trip;

  // ✅ SOLUSI: Real-time tracking tanpa throttling
  useEffect(() => {
    console.log('🚀 Driver location tracking effect triggered:', {
      bookingId,
      shouldTrack,
      bookingData: bookingData?.status,
      settings: settings?.share_trip
    });

    if (!bookingId || !shouldTrack) {
      console.log('🚀 Stopping tracking - conditions not met:', {
        bookingId: !!bookingId,
        shouldTrack
      });
      setIsTracking(false);
      return;
    }

    console.log('🚀 Starting REAL-TIME driver location tracking (NO THROTTLING) for booking:', bookingId);
    setIsTracking(true);

    // Clear existing listener
    if (realtimeUnsubscribe.current) {
      realtimeUnsubscribe.current();
      realtimeUnsubscribe.current = null;
    }

    // Get initial location
    FirebaseRealtime.getLatestDriverLocation(bookingId)
      .then(location => {
        if (location) {
          console.log('📍 Initial driver location:', location);
          setDriverLocation({
            lat: location.lat,
            lng: location.lng,
            timestamp: location.timestamp
          });
        }
      })
      .catch(error => {
        console.error('Error fetching initial driver location:', error);
        setError(error.message);
      });

    // ✅ SOLUSI: Real-time listener TANPA THROTTLING
    realtimeUnsubscribe.current = FirebaseRealtime.listenToDriverLocation(
      bookingId,
      (location) => {
        console.log('📍 REAL-TIME location update (NO THROTTLING):', location);
        setDriverLocation({
          lat: location.lat,
          lng: location.lng,
          timestamp: location.timestamp
        });
        setError(null);
      },
      (error) => {
        console.error('Error in real-time location tracking:', error);
        setError(error.message);
      }
    );

    // Cleanup
    return () => {
      if (realtimeUnsubscribe.current) {
        realtimeUnsubscribe.current();
        realtimeUnsubscribe.current = null;
      }
      if (bookingStatusUnsubscribe.current) {
        bookingStatusUnsubscribe.current();
        bookingStatusUnsubscribe.current = null;
      }
      setIsTracking(false);
    };
  }, [bookingId, shouldTrack]);

  return {
    driverLocation,
    isTracking,
    error,
    shouldTrack,
    bookingData
  };
};
